import React, { useState, useEffect, useCallback } from "react";
import NewInvoicePage from "./NewInvoicePage";

const API_BASE = process.env.REACT_APP_API_URL || "https://canada-billing.ameontech.com";

const STATUS_COLORS = {
  Paid:    { bg: "rgba(82,201,126,.13)",  color: "#52c97e", border: "rgba(82,201,126,.3)" },
  Unpaid:  { bg: "rgba(249,123,107,.13)", color: "#f97b6b", border: "rgba(249,123,107,.3)" },
  Draft:   { bg: "rgba(167,139,250,.13)", color: "#a78bfa", border: "rgba(167,139,250,.3)" },
  Overdue: { bg: "rgba(251,191,36,.13)",  color: "#f59e0b", border: "rgba(251,191,36,.3)" },
};

const EMPTY_FORM = {
  client: "", date: "", due: "", status: "Draft",
  items: [{ desc: "", qty: 1, rate: "" }],
};

function getToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  };
}

function Badge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Draft;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: "3px 11px", fontSize: 11, fontWeight: 700, letterSpacing: .4,
    }}>{status}</span>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: wide ? 780 : 560,
        maxHeight: "92vh", overflowY: "auto", padding: "28px 28px 24px",
        boxShadow: "0 24px 80px rgba(0,0,0,.18)",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 800, color: "#1a1a2e" }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontFamily: "'Playfair Display', serif", fontSize: 12, fontWeight: 800,
      color: "#4f8ef7", letterSpacing: 1, textTransform: "uppercase",
      borderBottom: "1.5px solid #f0f4ff", paddingBottom: 6, marginBottom: 12, marginTop: 20,
    }}>{children}</div>
  );
}

function InfoTile({ label, value, mono, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .6, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: 13, fontWeight: 600,
        color: accent ? "#4f8ef7" : "#1a1a2e",
        fontFamily: mono ? "'DM Mono', monospace" : "'DM Sans', sans-serif",
      }}>{value}</div>
    </div>
  );
}

function InvoiceDetail({ invoice, onClose, onEdit, onDelete }) {
  const items = (() => {
    if (Array.isArray(invoice.items)) return invoice.items;
    if (typeof invoice.items === "string") {
      try { return JSON.parse(invoice.items); } catch { return []; }
    }
    return [];
  })();

  const subtotal     = parseFloat(invoice.subtotal)      || items.reduce((s, it) => s + (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0), 0);
  const totalAmount  = parseFloat(invoice.total_amount)  || parseFloat(invoice.amount) || subtotal;
  const amountPaid   = parseFloat(invoice.amount_paid)   || 0;
  const balanceDue   = parseFloat(invoice.balance_due)   ?? Math.max(0, totalAmount - amountPaid);

  const fmt = v => `$${parseFloat(v||0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

  const hasTax     = invoice.gst_rate || invoice.pst_rate || invoice.hst_rate || invoice.total_tax;
  const hasSeller  = invoice.seller_business_name || invoice.seller_email || invoice.seller_phone || invoice.seller_address;
  const hasBuyer   = invoice.buyer_business_name  || invoice.buyer_email  || invoice.buyer_phone  || invoice.buyer_address;
  const hasPayment = invoice.payment_method || invoice.payment_terms || invoice.payment_details;
  const hasNotes   = invoice.notes || invoice.terms || invoice.footer_note;

  return (
    <Modal title={`Invoice ${invoice.invoice_number || invoice.id}`} onClose={onClose} wide>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "linear-gradient(135deg, #f8faff, #f0f4ff)",
        borderRadius: 12, padding: "12px 16px", marginBottom: 4,
        border: "1px solid #e8eeff",
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .7, textTransform: "uppercase", marginBottom: 4 }}>Invoice Number</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 800, color: "#4f8ef7" }}>
            {invoice.invoice_number || invoice.id}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .7, textTransform: "uppercase", marginBottom: 4 }}>Status</div>
          <Badge status={invoice.status} />
        </div>
      </div>

      <SectionTitle>Invoice Details</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 4 }}>
        <InfoTile label="Invoice Date"   value={invoice.date || invoice.invoice_date?.slice(0,10)} />
        <InfoTile label="Due Date"       value={invoice.due  || invoice.due_date?.slice(0,10)} />
        <InfoTile label="Currency"       value={invoice.currency || "CAD"} />
        <InfoTile label="Invoice Prefix" value={invoice.invoice_prefix} />
      </div>

      {hasSeller && (
        <>
          <SectionTitle>Seller (Your Business)</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <InfoTile label="Business Name" value={invoice.seller_business_name} />
            <InfoTile label="Email"         value={invoice.seller_email} />
            <InfoTile label="Phone"         value={invoice.seller_phone} />
            <InfoTile label="Address"       value={[invoice.seller_address, invoice.seller_city, invoice.seller_province, invoice.seller_postal_code, invoice.seller_country].filter(Boolean).join(", ")} />
          </div>
        </>
      )}

      {hasBuyer && (
        <>
          <SectionTitle>Bill To (Client)</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <InfoTile label="Business Name" value={invoice.buyer_business_name || invoice.client} />
            <InfoTile label="Email"         value={invoice.buyer_email} />
            <InfoTile label="Phone"         value={invoice.buyer_phone} />
            <InfoTile label="Tax Number"    value={invoice.buyer_tax_number} />
            <InfoTile label="Address"       value={[invoice.buyer_address, invoice.buyer_city, invoice.buyer_province, invoice.buyer_postal_code, invoice.buyer_country].filter(Boolean).join(", ")} />
          </div>
        </>
      )}

      <SectionTitle>Line Items</SectionTitle>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 4 }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #f0f0f0" }}>
            {["Description", "Qty", "Rate", "Amount"].map(h => (
              <th key={h} style={{
                padding: "6px 8px", textAlign: h === "Description" ? "left" : "right",
                fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .6, textTransform: "uppercase",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={4} style={{ padding: "14px 8px", color: "#ccc", fontSize: 12, textAlign: "center" }}>No line items</td></tr>
          ) : items.map((it, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
              <td style={{ padding: "9px 8px", color: "#1a1a2e" }}>{it.desc || it.description || ""}</td>
              <td style={{ padding: "9px 8px", textAlign: "right", color: "#555" }}>{it.qty}</td>
              <td style={{ padding: "9px 8px", textAlign: "right", color: "#555", fontFamily: "'DM Mono', monospace" }}>${parseFloat(it.rate||0).toFixed(2)}</td>
              <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'DM Mono', monospace", fontWeight: 600, color: "#1a1a2e" }}>${((parseFloat(it.qty)||0)*(parseFloat(it.rate)||0)).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle>Totals & Tax</SectionTitle>
      <div style={{ background: "#f8fafc", borderRadius: 12, padding: "14px 16px", marginBottom: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#888", fontWeight: 600 }}>Subtotal</span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#1a1a2e" }}>{fmt(subtotal)}</span>
        </div>
        {hasTax && (
          <>
            {invoice.gst_rate > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "#888" }}>GST ({invoice.gst_rate}%)</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#555" }}>{fmt(invoice.gst_amount)}</span>
              </div>
            )}
            {invoice.pst_rate > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "#888" }}>PST ({invoice.pst_rate}%)</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#555" }}>{fmt(invoice.pst_amount)}</span>
              </div>
            )}
            {invoice.hst_rate > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "#888" }}>HST ({invoice.hst_rate}%){invoice.hst_registration_number ? ` — Reg# ${invoice.hst_registration_number}` : ""}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#555" }}>{fmt(invoice.hst_amount)}</span>
              </div>
            )}
            {invoice.total_tax > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, borderTop: "1px dashed #e8e8e8", paddingTop: 6 }}>
                <span style={{ fontSize: 12, color: "#888" }}>Total Tax</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#555" }}>{fmt(invoice.total_tax)}</span>
              </div>
            )}
          </>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "2px solid #e8eeff", paddingTop: 10, marginTop: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#1a1a2e" }}>Total (CAD)</span>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 800, color: "#4f8ef7" }}>{fmt(totalAmount)}</span>
        </div>
        {amountPaid > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ fontSize: 12, color: "#52c97e", fontWeight: 600 }}>Amount Paid</span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#52c97e", fontWeight: 700 }}>{fmt(amountPaid)}</span>
          </div>
        )}
        {balanceDue > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ fontSize: 12, color: "#f97b6b", fontWeight: 600 }}>Balance Due</span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#f97b6b", fontWeight: 700 }}>{fmt(balanceDue)}</span>
          </div>
        )}
      </div>

      {hasPayment && (
        <>
          <SectionTitle>Payment Information</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <InfoTile label="Payment Method"  value={invoice.payment_method} />
            <InfoTile label="Payment Terms"   value={invoice.payment_terms} />
            <InfoTile label="Payment Details" value={invoice.payment_details} />
          </div>
        </>
      )}

      {hasNotes && (
        <>
          <SectionTitle>Notes & Terms</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            {invoice.notes && (
              <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .6, textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>{invoice.notes}</div>
              </div>
            )}
            {invoice.terms && (
              <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .6, textTransform: "uppercase", marginBottom: 4 }}>Terms & Conditions</div>
                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.6 }}>{invoice.terms}</div>
              </div>
            )}
            {invoice.footer_note && (
              <div style={{ background: "#fffbf0", borderRadius: 10, padding: "10px 14px", border: "1px solid #fde9a2" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#f59e0b", letterSpacing: .6, textTransform: "uppercase", marginBottom: 4 }}>Footer Note</div>
                <div style={{ fontSize: 12, color: "#888", lineHeight: 1.6 }}>{invoice.footer_note}</div>
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
        {/* ✅ FIX: Delete ला नेहमी dbId (real UUID) पाठवा */}
        <button onClick={() => onDelete(invoice.dbId || invoice.id)} style={{
          padding: "8px 18px", borderRadius: 50, border: "1.5px solid rgba(249,123,107,.4)",
          background: "transparent", color: "#f97b6b", fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>Delete</button>
        <button onClick={() => onEdit(invoice)} style={{
          padding: "8px 18px", borderRadius: 50,
          background: "linear-gradient(135deg, #4f8ef7, #6366f1)",
          border: "none", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
        }}>Edit Invoice</button>
      </div>
    </Modal>
  );
}

function InvoiceForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem  = (i, k, v) => setForm(f => ({
    ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it),
  }));
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { desc: "", qty: 1, rate: "" }] }));
  const delItem = i  => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const total   = form.items.reduce((s, it) => s + (parseFloat(it.qty)||0)*(parseFloat(it.rate)||0), 0);

  const inp = {
    width: "100%", padding: "9px 12px", border: "1.5px solid #f0f0f0",
    borderRadius: 9, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
    outline: "none", color: "#1a1a2e", background: "#fafafa", marginTop: 4,
  };
  const lbl = { fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: .6, textTransform: "uppercase" };
  const row = { marginBottom: 14 };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <div style={row}>
          <div style={lbl}>Client Name</div>
          <input style={inp} value={form.client} onChange={e => setField("client", e.target.value)} placeholder="e.g. Maple Tech" />
        </div>
        <div style={row}>
          <div style={lbl}>Status</div>
          <select style={inp} value={form.status} onChange={e => setField("status", e.target.value)}>
            {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div style={row}>
          <div style={lbl}>Invoice Date</div>
          <input style={inp} type="date" value={form.date} onChange={e => setField("date", e.target.value)} />
        </div>
        <div style={row}>
          <div style={lbl}>Due Date</div>
          <input style={inp} type="date" value={form.due} onChange={e => setField("due", e.target.value)} />
        </div>
      </div>

      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 10 }}>Line Items</div>
      {form.items.map((it, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 60px 90px 28px", gap: 7, marginBottom: 8, alignItems: "center" }}>
          <input style={inp} value={it.desc} onChange={e => setItem(i, "desc", e.target.value)} placeholder="Description" />
          <input style={inp} type="number" value={it.qty}  onChange={e => setItem(i, "qty",  e.target.value)} placeholder="Qty" min={1} />
          <input style={inp} type="number" value={it.rate} onChange={e => setItem(i, "rate", e.target.value)} placeholder="Rate $" />
          <button onClick={() => delItem(i)} style={{ background: "none", border: "none", color: "#f97b6b", fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>
      ))}
      <button onClick={addItem} style={{ background: "rgba(79,142,247,.08)", border: "1.5px dashed rgba(79,142,247,.35)", borderRadius: 9, padding: "7px 16px", fontSize: 12, fontWeight: 600, color: "#4f8ef7", cursor: "pointer", marginBottom: 18 }}>+ Add Line Item</button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
        <span style={{ fontSize: 12, color: "#888", fontWeight: 600 }}>TOTAL (CAD)</span>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>${total.toLocaleString("en-CA", { minimumFractionDigits: 2 })}</span>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 50, border: "1.5px solid #f0f0f0", background: "transparent", fontSize: 13, fontWeight: 600, color: "#555", cursor: "pointer" }}>Cancel</button>
        <button onClick={() => onSave({ ...form, amount: total })} style={{ padding: "9px 20px", borderRadius: 50, background: "linear-gradient(135deg, #4f8ef7, #6366f1)", border: "none", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Save Invoice</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════
export default function InvoiceManagement() {
  const [invoices,    setInvoices]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [filter,      setFilter]      = useState("All");
  const [showForm,    setShowForm]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [viewing,     setViewing]     = useState(null);
  const [showNewPage, setShowNewPage] = useState(false);
  const [toast,       setToast]       = useState(null);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/invoices`, { headers: authHeaders() });
      if (!res.ok) throw new Error("Fetch failed");
      const data = await res.json();

      const mapped = (data.invoices || []).map(inv => ({
        ...inv,
        // ✅ id = display साठी invoice_number (e.g. "INV-004")
        // ✅ dbId = database साठी real UUID
        id:     inv.invoice_number || inv.id,
        dbId:   inv.id,
        client: inv.buyer_business_name || inv.client || "",
        date:   inv.invoice_date ? inv.invoice_date.slice(0, 10) : "",
        due:    inv.due_date     ? inv.due_date.slice(0, 10)     : "",
        amount: parseFloat(inv.total_amount) || 0,
        status: inv.status || "Draft",
        items:  Array.isArray(inv.items)
                  ? inv.items
                  : (typeof inv.items === "string" ? (() => { try { return JSON.parse(inv.items || "[]"); } catch { return []; } })() : []),
      }));

      setInvoices(mapped);
    } catch (err) {
      console.error("Invoice fetch error:", err);
      showToast("Invoices load करताना error आली");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const invoicePrefix = (() => {
    if (invoices.length === 0) return "INV";
    const match = String(invoices[0].id).match(/^([A-Z]+)-/);
    return match ? match[1] : "INV";
  })();

  const nextId = () => {
    if (invoices.length === 0) return `${invoicePrefix}-001`;
    const nums = invoices.map(inv => {
      const match = String(inv.id).match(/(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    });
    return `${invoicePrefix}-${String(Math.max(...nums) + 1).padStart(3, "0")}`;
  };

  const handleNewSave = async () => {
    await fetchInvoices();
    setShowNewPage(false);
    setEditing(null);
    showToast(editing ? "Invoice updated successfully" : "Invoice created successfully");
  };

  const handleSave = async (form) => {
    try {
      if (editing) {
        // ✅ editing.dbId = real UUID वापरतो PUT साठी
        const res = await fetch(`${API_BASE}/api/invoices/${editing.dbId}`, {
          method: "PUT",
          headers: authHeaders(),
          body: JSON.stringify({
            invoice_number:      editing.id,
            buyer_business_name: form.client,
            invoice_date:        form.date,
            due_date:            form.due,
            status:              form.status,
            total_amount:        form.amount,
            items:               form.items,
          }),
        });
        if (!res.ok) throw new Error("Update failed");
        showToast("Invoice updated successfully");
      } else {
        const res = await fetch(`${API_BASE}/api/invoices`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            invoice_number:      nextId(),
            buyer_business_name: form.client,
            invoice_date:        form.date,
            due_date:            form.due,
            status:              form.status,
            total_amount:        form.amount,
            items:               form.items,
          }),
        });
        if (!res.ok) throw new Error("Create failed");
        showToast("Invoice created successfully");
      }
      await fetchInvoices();
    } catch (err) {
      console.error("Save error:", err);
      showToast("Save करताना error आली");
    } finally {
      setShowForm(false);
      setEditing(null);
    }
  };

  const handleDelete = async (id) => {
    // ✅ id हा dbId (UUID) असेल असे गृहित धरतो — InvoiceDetail मधून dbId पाठवतो
    const inv = invoices.find(i => i.dbId === id || i.id === id);
    if (!inv) return;
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${inv.dbId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Delete failed");
      await fetchInvoices();
      setViewing(null);
      showToast("Invoice deleted");
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Delete करताना error आली");
    }
  };

  // ✅ KEY FIX: editInvoice ला id = dbId (real UUID) set करतो
  // NewInvoicePage (CanadaInvoiceGenerator) मध्ये `editInvoice.id` वापरून
  // PUT request जाते — त्याला UUID हवा, "INV-004" नाही
  const handleEdit = (inv) => {
    setEditing({
      ...inv,
      id: inv.dbId,             // ✅ UUID → database PUT साठी
      invoice_number: inv.invoice_number || inv.id, // ✅ display साठी "INV-004" जपतो
    });
    setViewing(null);
    setShowNewPage(true);
  };

  const handleView = async (inv) => {
    setViewing(inv);
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${inv.dbId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.invoice) {
          const full = {
            ...data.invoice,
            id:     data.invoice.invoice_number || data.invoice.id,
            dbId:   data.invoice.id,
            client: data.invoice.buyer_business_name || inv.client,
            date:   data.invoice.invoice_date ? data.invoice.invoice_date.slice(0,10) : inv.date,
            due:    data.invoice.due_date     ? data.invoice.due_date.slice(0,10)     : inv.due,
            amount: parseFloat(data.invoice.total_amount) || inv.amount,
            status: data.invoice.status || inv.status,
            items:  Array.isArray(data.invoice.items)
                      ? data.invoice.items
                      : (typeof data.invoice.items === "string"
                          ? (() => { try { return JSON.parse(data.invoice.items||"[]"); } catch { return []; } })()
                          : inv.items),
          };
          setViewing(full);
        }
      }
    } catch (err) {
      console.error("Full invoice fetch error:", err);
    }
  };

  const filtered = invoices.filter(inv => {
    const matchSearch =
      (inv.client?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (inv.id?.toLowerCase()     || "").includes(search.toLowerCase());
    const matchFilter = filter === "All" || inv.status === filter;
    return matchSearch && matchFilter;
  });

  const stats = {
    total:  invoices.reduce((s, i) => s + (i.amount || 0), 0),
    paid:   invoices.filter(i => i.status === "Paid").reduce((s, i) => s + (i.amount || 0), 0),
    unpaid: invoices.filter(i => i.status === "Unpaid" || i.status === "Overdue").reduce((s, i) => s + (i.amount || 0), 0),
    count:  invoices.length,
  };

  const cardStyle = {
    background: "#fff", borderRadius: 14, padding: "18px 20px",
    border: "1px solid #f0f0f0", flex: 1, minWidth: 0,
  };

  if (showNewPage) {
    return (
      <NewInvoicePage
        onSave={handleNewSave}
        onCancel={() => { setShowNewPage(false); setEditing(null); }}
        editInvoice={editing}
      />
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>

      <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
        {[
          { label: "Total Invoiced", val: `$${stats.total.toLocaleString("en-CA", { minimumFractionDigits: 2 })}`, color: "#4f8ef7", icon: "🗒" },
          { label: "Collected",      val: `$${stats.paid.toLocaleString("en-CA",  { minimumFractionDigits: 2 })}`, color: "#52c97e", icon: "✅" },
          { label: "Outstanding",    val: `$${stats.unpaid.toLocaleString("en-CA",{ minimumFractionDigits: 2 })}`, color: "#f97b6b", icon: "⏳" },
          { label: "Total Invoices", val: stats.count, color: "#a78bfa", icon: "📋" },
        ].map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", letterSpacing: .7, textTransform: "uppercase", marginBottom: 8 }}>{c.icon} {c.label}</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 20, fontWeight: 700, color: c.color }}>{c.val}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search invoices or clients…"
          style={{ flex: 1, minWidth: 180, padding: "9px 14px", border: "1.5px solid #f0f0f0", borderRadius: 50, fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: "none", background: "#fafafa", color: "#1a1a2e" }}
        />
        <div style={{ display: "flex", gap: 5 }}>
          {["All", ...Object.keys(STATUS_COLORS)].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: "7px 14px", borderRadius: 50, fontSize: 12, fontWeight: 600, cursor: "pointer",
              border: filter === s ? "none" : "1.5px solid #f0f0f0",
              background: filter === s ? "linear-gradient(135deg, #4f8ef7, #6366f1)" : "transparent",
              color: filter === s ? "#fff" : "#888",
            }}>{s}</button>
          ))}
        </div>
        <button
          onClick={() => { setEditing(null); setShowNewPage(true); }}
          style={{ padding: "9px 18px", borderRadius: 50, background: "linear-gradient(135deg, #4f8ef7, #6366f1)", border: "none", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
        >+ New Invoice</button>
      </div>

      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #f5f5f5", background: "#fafafa" }}>
              {["Invoice #", "Client", "Date", "Due Date", "Amount (CAD)", "Status", ""].map(h => (
                <th key={h} style={{ padding: "11px 14px", textAlign: h === "Amount (CAD)" ? "right" : "left", fontSize: 10, fontWeight: 700, color: "#aaa", letterSpacing: .7, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 60, color: "#ccc", fontSize: 14 }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
                  <div style={{ fontWeight: 600, color: "#aaa" }}>Invoices load होत आहेत...</div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 60, color: "#ccc", fontSize: 14 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🧾</div>
                  <div style={{ fontWeight: 600, color: "#aaa", marginBottom: 6 }}>No invoices yet</div>
                  <div style={{ fontSize: 12, color: "#ccc" }}>Click "+ New Invoice" to create your first invoice</div>
                </td>
              </tr>
            ) : filtered.map(inv => (
              <tr key={inv.dbId || inv.id}
                style={{ borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background .1s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fafbff"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <td style={{ padding: "12px 14px", fontFamily: "'DM Mono', monospace", fontWeight: 600, color: "#4f8ef7", fontSize: 12 }} onClick={() => handleView(inv)}>{inv.id}</td>
                <td style={{ padding: "12px 14px", fontWeight: 600, color: "#1a1a2e" }} onClick={() => handleView(inv)}>{inv.client}</td>
                <td style={{ padding: "12px 14px", color: "#888" }} onClick={() => handleView(inv)}>{inv.date}</td>
                <td style={{ padding: "12px 14px", color: inv.status === "Overdue" ? "#f59e0b" : "#888", fontWeight: inv.status === "Overdue" ? 700 : 400 }} onClick={() => handleView(inv)}>{inv.due}</td>
                <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: "'DM Mono', monospace", fontWeight: 700, color: "#1a1a2e" }} onClick={() => handleView(inv)}>${(inv.amount||0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}</td>
                <td style={{ padding: "12px 14px" }} onClick={() => handleView(inv)}><Badge status={inv.status} /></td>
                <td style={{ padding: "12px 14px" }}>
                  <button onClick={() => handleEdit(inv)} style={{ background: "rgba(79,142,247,.08)", border: "1px solid rgba(79,142,247,.2)", borderRadius: 8, padding: "5px 11px", fontSize: 11, fontWeight: 600, color: "#4f8ef7", cursor: "pointer" }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: "#bbb", textAlign: "right" }}>
          Showing {filtered.length} of {invoices.length} invoices
        </div>
      )}

      {showForm && (
        <Modal title={editing ? `Edit ${editing.invoice_number || editing.id}` : "New Invoice"} onClose={() => { setShowForm(false); setEditing(null); }}>
          <InvoiceForm
            initial={editing}
            onSave={handleSave}
            onClose={() => { setShowForm(false); setEditing(null); }}
          />
        </Modal>
      )}

      {viewing && (
        <InvoiceDetail
          invoice={viewing}
          onClose={() => setViewing(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 999,
          background: "#13131f", border: "1px solid rgba(255,255,255,.1)",
          borderLeft: "3px solid #52c97e", borderRadius: 12,
          padding: "13px 18px", fontSize: 13, fontWeight: 500, color: "#fff",
          boxShadow: "0 8px 32px rgba(0,0,0,.25)",
        }}>✓ {toast}</div>
      )}
    </div>
  );
}